// ============================================================================
// Bet settlement against the RapidOddsAPI results feed.
//
// Pure functions only — no React, no network, no Supabase. Everything here
// takes a bet plus a results game and returns a verdict, which keeps the rules
// testable against real API payloads.
//
// GUIDING RULE: never settle on a guess. Every function below returns null
// rather than a verdict when anything is missing or ambiguous, which leaves the
// bet pending for the user to settle by hand. A bet stuck on pending is a minor
// annoyance; a bet silently settled the wrong way corrupts their P&L.
// ============================================================================

// Results timestamps are naive UTC with no offset. Date() would read them in
// the browser's local zone, which in Sydney is a 10-hour error, so the Z is
// mandatory on every single parse.
const asMs = (naiveUtc) => new Date(`${naiveUtc}Z`).getTime()

// Same threshold the bookmaker-matching engine uses. Two teams can meet more
// than once in a season, so the pair alone does not identify a fixture; the
// window disambiguates. Feeds also disagree by seconds on start time.
export const MATCH_WINDOW_HOURS = 4.0

// How long after a game is first seen final before we trust its numbers. The
// results scrapers keep re-checking for post-game stat corrections for 2 hours
// and only then lock the game, so anything shorter can settle off a figure that
// later changes. Raising this toward 2h is the fix if a prop ever settles wrong.
export const SETTLE_DELAY_MS = 60 * 60 * 1000

// What each sport calls its unit of score. AFL scores points, baseball scores
// runs, so nothing here may hardcode "points". A sport absent from this map is
// never settled for team markets, which is the safe default when adding one.
//
// Player props need no entry at all: the results feed names player stat fields
// after the market key itself (player_disposals -> players[].player_disposals),
// so they work for any sport the moment its results ship.
const SPORT_SCORE_FIELD = {
  AFL:  'points',
  MLB:  'runs',
  NRL:  'points',
  WNBA: 'points',
  NFL:  'points',
  NBA:  'points'
}

// The period suffixes markets actually carry. This is the complete vocabulary
// as of 2026-07: grep markets.js before adding to it.
//
// A suffix missing here is not a silent wrong answer: the whole key falls
// through as the base, marketKind stops recognising it, and the bet stays
// pending. That is what `alternate_total_runs_1st_1_innings` did before
// `1st_1_innings` was listed.
const WINDOW_SUFFIXES = ['1st_half', '1st_5_innings', '1st_1_innings', '1st_period', '1st_set']

// What to call the moment a window closes, for the row that is decided but not
// yet settled. Full game uses "FT" and is handled separately.
const WINDOW_END_LABEL = {
  '1st_half':      'HT',
  '1st_period':    'P1 END',
  '1st_5_innings': 'F5 END',
  '1st_1_innings': 'INN 1 END',
  '1st_set':       'SET 1'
}

// How each sport divides a game: the live period bar, the period label, and
// which periods each market window covers. A sport missing here still shows its
// bet meter, just without game context, and cannot resolve a period market.
//
// `clock` is a formatter rather than a flag because sports disagree on what a
// clock even is: AFL counts seconds up within a quarter, baseball has no clock
// at all and only a top/middle/bottom state. It is handed the WHOLE period-state
// row rather than a number, since what each sport calls its live position sits
// in a different field. Returning null means "this sport has no clock", which
// the bar renders as period only.
//
// Structural shapes first, applied to leagues below. Nine soccer leagues share
// one structure and repeating it nine times is nine chances to mistype it.
//
// `windows` is the whole point of this file's period handling: "1st_half" means
// periods 1 and 2 in a four-quarter sport but only period 1 in a two-half one,
// so the same market key resolves differently per sport. A window a sport does
// not declare is simply unreadable, and the bet stays manual.
//
// `fraction` is the SECOND formatter, and it is separate from `clock` on
// purpose. `clock` returns the sport's own display string ("8:57", "43'"),
// which is a per-sport FORMAT and cannot be measured; `fraction` returns how
// far through the period we are, 0 to 1, which needs a NUMBER. The feed ships
// both for exactly this reason, so nothing here ever parses a display string
// back into seconds to recover a value sitting in the next field along.
//
// Like `clock` it takes the whole period-state row, since each sport's number
// lives in a differently named field and counts a different way. Returning null
// means "cannot measure this", which the bar renders as an empty segment rather
// than a guess.
//
// `clock` and `fraction` both default to none. Every sport that overrides them
// is documented at its own entry below.
const QUARTERS = { segments: 4, label: (p) => `Q${p}`,     windows: { '1st_half': [1, 2] }, clock: () => null, fraction: () => null }
const HALVES   = { segments: 2, label: (p) => `H${p}`,     windows: { '1st_half': [1] },    clock: () => null, fraction: () => null }
// Tennis splits on match format, not on tour. The Slams are best-of-5 and every
// other ATP event is best-of-3, and `segments` is the period bar's COLUMN COUNT,
// so giving a tour event five would leave two columns that can never fill.
// Display only — nothing reads segments to settle a bet.
const SETS_5   = { segments: 5, label: (p) => `Set ${p}`,  windows: { '1st_set': [1] },     clock: () => null, fraction: () => null }
const SETS_3   = { ...SETS_5, segments: 3 }

// Period lengths, in seconds of real time. Only sports whose feed ships a
// measurable clock need one.
const AFL_QUARTER   = 30 * 60   // wall clock, not the 20 min game clock — see AFL below
const WNBA_QUARTER  = 10 * 60
const WNBA_OVERTIME = 5 * 60
const NBA_QUARTER   = 12 * 60   // NBA quarters are 12 min against WNBA's 10; OT is 5 in both
const NRL_HALF      = 40 * 60
const NFL_QUARTER   = 15 * 60
const NFL_OVERTIME  = 10 * 60   // regular season OT, shorter than a quarter

const SOCCER_LEAGUES = ['MLS', 'BUNDESLIGA', 'UCL', 'EPL', 'LA_LIGA', 'SERIE_A', 'LIGUE_1', 'A_LEAGUE', 'WORLD_CUP']
// Tennis, split by match format. A sport missing from both lists can resolve no
// period market and draws no meter.
//
// The four retired MENS_* Slam ids stay in the best-of-5 list: a bet tracked
// before the Aug 2026 ATP_* rename still carries one, and dropping them would
// strip the set windows off bets already in user_bets.
const TENNIS_BO5 = [
  'ATP_AUSTRALIAN_OPEN', 'ATP_FRENCH_OPEN', 'ATP_WIMBLEDON', 'ATP_US_OPEN',
  'MENS_AO', 'MENS_RG', 'MENS_WIMBLEDON', 'MENS_USO',
]
const TENNIS_BO3 = [
  'ATP_INDIAN_WELLS', 'ATP_MIAMI', 'ATP_MONTE_CARLO', 'ATP_MADRID', 'ATP_ROME',
  'ATP_CANADA', 'ATP_CINCINNATI', 'ATP_SHANGHAI', 'ATP_PARIS', 'ATP_FINALS',
  // Challenger is best-of-3 like the Masters events above. Inert until a tennis
  // results feed exists (results-api.py carries no tennis today, so NO ATP bet
  // auto-settles or draws a meter) - correct value, set now so it is not missed.
  'ATP_CHALLENGER',
]
const spread = (leagues, shape) => Object.fromEntries(leagues.map(l => [l, shape]))

const SPORT_PERIODS = {
  ...spread(SOCCER_LEAGUES, HALVES),
  ...spread(TENNIS_BO5, SETS_5),
  ...spread(TENNIS_BO3, SETS_3),

  // Same feed shape and DOWN-counting clock as WNBA below, but a 12 minute
  // quarter. Reusing WNBA's 600 would put the bar below zero for the first 2 min.
  NBA: {
    ...QUARTERS,
    clock: (state) => state?.display_clock ?? null,
    fraction: (state) => {
      const remaining = state?.period_seconds_remaining
      if (remaining == null || state?.period == null) return null
      const length = state.period > 4 ? WNBA_OVERTIME : NBA_QUARTER
      return 1 - (remaining / length)
    }
  },

  // Gridiron's clock counts DOWN within the quarter, 900 to 0, the same
  // direction as basketball and the opposite of AFL/NRL. So the fraction is
  // INVERTED here too: a quarter reading 11:44 remaining is a fifth played,
  // not four fifths.
  //
  // The quarter is 900s against WNBA's 600, and OVERTIME is 600s against
  // WNBA's 300 — both numbers are NFL's own. Copying basketball's lengths
  // would leave every bar reading high, and an OT period would show full
  // two thirds of the way through.
  NFL: {
    ...QUARTERS,
    clock: (state) => state?.display_clock ?? null,
    fraction: (state) => {
      const remaining = state?.period_seconds_remaining
      if (remaining == null || state?.period == null) return null
      const length = state.period > 4 ? NFL_OVERTIME : NFL_QUARTER
      return 1 - (remaining / length)
    }
  },

  // Rugby league's clock counts UP and is WHOLE-MATCH elapsed: 2390 at half
  // time, 4800 at full time. It does NOT reset each half, so the current half's
  // elapsed time has to be recovered by subtracting the halves already played.
  // Reading it as if it were AFL's per-period field would show the second half
  // permanently full from the moment it kicked off.
  //
  // Golden point adds periods past the second, which this measures against a
  // 40 minute half and so reads low. Harmless rather than wrong: the bar draws
  // `segments` (2) columns and only fills the one matching `period`, so a third
  // period's fraction is never rendered at all.
  NRL: {
    ...HALVES,
    clock: (state) => state?.display_clock ?? null,
    fraction: (state) => {
      const elapsed = state?.match_seconds_elapsed
      if (elapsed == null || state?.period == null) return null
      return (elapsed - NRL_HALF * (state.period - 1)) / NRL_HALF
    }
  },

  // Basketball's clock counts DOWN, 600 to 0 within the quarter, which is the
  // opposite of every other sport here. The fraction is therefore INVERTED: a
  // quarter reading 8:57 remaining is a tenth played, not nine tenths.
  //
  // Overtime periods are 5 minutes rather than 10, so anything past the fourth
  // measures against the shorter length. Without that an OT period would sit at
  // half full at the moment it expired.
  WNBA: {
    ...QUARTERS,
    clock: (state) => state?.display_clock ?? null,
    fraction: (state) => {
      const remaining = state?.period_seconds_remaining
      if (remaining == null || state?.period == null) return null
      const length = state.period > 4 ? WNBA_OVERTIME : WNBA_QUARTER
      return 1 - (remaining / length)
    }
  },

  // Three periods cannot split into halves, so it offers only its own window.
  NHL:  { segments: 3, label: (p) => `P${p}`, windows: { '1st_period': [1] }, clock: () => null },

  // Innings, and the standard "first five" market rather than a half.
  //
  // No clock at all: ESPN reports 0:00 on a live baseball game, so the scraper
  // sends WHICH HALF of the current inning is being played instead, off
  // status.type.detail ("Top 7th" -> Top / Bottom / Middle / End). It is present
  // ONLY on the inning in progress, which is exactly right — a finished inning
  // has no half worth naming, and `?? null` there means the header falls back to
  // the inning alone rather than printing a stale one.
  //
  // `segments: 9` is the regulation game. Extra innings still settle correctly
  // (nothing reads segments to decide anything); the period bar simply stops
  // drawing at nine.
  MLB:  {
    segments: 9,
    label: (p) => `Inn ${p}`,
    windows: { '1st_5_innings': [1, 2, 3, 4, 5], '1st_1_innings': [1] },
    clock: (state) => state?.half ?? null
  },

  AFL: {
    ...QUARTERS,
    label: (period) => `Q${period}`,

    // `period_seconds_elapsed` is REAL elapsed time in the quarter, counting up
    // and resetting each one. Verified 2026-07-27 against concluded game
    // CD_M20260142007, whose four completed quarters read 1770 / 1774 / 1970 /
    // 1689. A quarter is 20 minutes of game clock, but stoppages stretch that to
    // roughly 28-33 minutes of wall clock, so 30 is the honest midpoint for a
    // fill fraction. Individual quarters run either side of it and the bar
    // clamps rather than overflowing.
    //
    // The bare `seconds` fallback is for AFL games scraped before that field was
    // renamed: concluded docs are locked from rewriting, so they keep the old
    // key until retention ages them out. Safe to delete once it has.
    fraction: (state) => {
      const elapsed = state?.period_seconds_elapsed ?? state?.seconds
      return elapsed == null ? null : elapsed / AFL_QUARTER
    },

    // No clock on purpose. AFL's own app counts DOWN from 20:00 of game clock,
    // pausing at stoppages, and that value is not `periodSeconds` — deriving a
    // countdown from elapsed wall time would be wrong by up to ten minutes.
    //
    // TODO(live-clock): the real game clock may sit in matchClock's top-level
    // fields, which came back empty `{}` on a concluded game. While a game is
    // actually LIVE, run test_afl_live.py in the scrapers repo and read them. If
    // the clock is there, surface it on period_state and format it here — this
    // formatter is handed the whole state row, so a new field needs no plumbing.
    // See "Live clock" under Not implemented in CLAUDE.md.
    clock: () => null
  }
}

/** Splits a market key into its base family and its period window, if any. */
function splitMarket(market) {
  for (const w of WINDOW_SUFFIXES) {
    if (market?.endsWith(`_${w}`)) {
      return { base: market.slice(0, -(w.length + 1)), windowName: w }
    }
  }
  return { base: market ?? null, windowName: null }
}

/**
 * What kind of running value a market family carries.
 *
 * Matched on PREFIX rather than enumerated, because the unit is baked into the
 * key and every sport spells its own: alternate_total_points / _goals / _tries /
 * _runs / _corners / _cards all behave identically. Enumerating them would mean
 * a config edit per sport for no gain.
 */
function marketKind(base) {
  if (!base) return null
  if (base === 'head_to_head' || base === 'alternate_lines') return 'margin'
  if (base.startsWith('alternate_team_total_')) return 'team_total'
  if (base.startsWith('alternate_total_')) return 'match_total'
  return null
}

const METER_FOR_KIND = { margin: 'margin', team_total: 'progress', match_total: 'progress' }

/**
 * What unit a total market counts, taken from the key itself.
 *
 * A single per-sport score field cannot serve a sport that prices more than one
 * thing. The NRL offers both alternate_total_points and alternate_total_tries;
 * reading tries off the points column would settle confidently and wrongly.
 * SPORT_SCORE_FIELD stays for margin markets, where "score" really is one thing
 * per sport.
 */
function unitOf(base) {
  const m = /^alternate_(?:team_)?total_(.+)$/.exec(base ?? '')
  return m ? m[1] : null
}

/**
 * The periods a bet covers: null for a whole-game market, a list for a period
 * one. `unreadable` flags a period market whose sport never declared that
 * window, which must never fall through and be settled as a full-game bet.
 *
 * This is what makes period markets correct across sports. "1st half" is two
 * quarters in AFL but one half in the NRL, so a resolver hardcoding periods
 * 1 and 2 silently settles an NRL first-half bet off the whole match.
 */
function windowFor(bet) {
  const { base, windowName } = splitMarket(bet?.base_market_type)
  if (!windowName) return { base, periods: null, unreadable: false }
  const periods = SPORT_PERIODS[bet?.sport]?.windows?.[windowName] ?? null
  return { base, periods, unreadable: !periods }
}

/**
 * Sum a by-period list over a window, or over everything when periods is null.
 *
 * Returns null if ANY period of the window is missing rather than summing what
 * is there: a half with one quarter recorded is not a half, and a number that
 * looks plausible is far more dangerous here than no number at all.
 */
function sumWindow(rows, periods, field) {
  if (!Array.isArray(rows) || !field) return null

  const wanted = periods
    ? periods.map(n => rows.find(r => r.period === n))
    : rows

  // A period missing entirely, or missing THIS unit, means the feed cannot
  // answer the question. Treating an absent field as zero is the dangerous
  // reading: a tries market on a feed that only records points would settle
  // every bet as Under 0 rather than staying pending.
  if (wanted.length === 0) return null
  if (wanted.some(r => !r || r[field] == null)) return null

  return wanted.reduce((sum, r) => sum + r[field], 0)
}

function teamBlock(score, team) {
  if (!score || !team) return null
  if (score.home?.team === team) return score.home
  if (score.away?.team === team) return score.away
  return null
}

function teamPeriods(score, team) {
  if (!score || !team) return null
  if (score.home?.team === team) return score.home_by_period ?? null
  if (score.away?.team === team) return score.away_by_period ?? null
  return null
}

// ── Matching ────────────────────────────────────────────────────────────────

/** True when the bet's two teams are the same pair as the game's, either way round. */
function teamsMatch(bet, game) {
  const betPair = [bet.home_team, bet.away_team]
  const gamePair = [game.home_team, game.away_team]
  if (betPair.some(t => !t) || gamePair.some(t => !t)) return false
  return betPair.every(t => gamePair.includes(t))
}

/**
 * Find the results game a bet refers to: same teams, nearest start time inside
 * the window. Nearest rather than first, so a same-day double header cannot
 * resolve to the wrong fixture. Returns null when nothing is close enough.
 */
export function findGameForBet(bet, games) {
  if (!bet?.commence_time || !Array.isArray(games)) return null

  const betMs = asMs(bet.commence_time)
  if (Number.isNaN(betMs)) return null

  let best = null
  let bestGapHours = Infinity

  for (const entry of games) {
    const game = entry?.game ?? entry
    if (!game?.commence_time || !teamsMatch(bet, game)) continue

    const gapHours = Math.abs(asMs(game.commence_time) - betMs) / 3600000
    if (gapHours < bestGapHours) {
      bestGapHours = gapHours
      best = entry
    }
  }

  return bestGapHours <= MATCH_WINDOW_HOURS ? best : null
}

/**
 * Whether a game's numbers are final enough to settle against. Concluded, plus
 * past the stat-correction delay. Games with no concluded_at (they finished
 * before the field existed) are deliberately not settleable.
 */
export function isSettleable(game, now = Date.now()) {
  if (game?.status !== 'CONCLUDED' || !game?.concluded_at) return false
  const concludedMs = asMs(game.concluded_at)
  if (Number.isNaN(concludedMs)) return false
  return now - concludedMs >= SETTLE_DELAY_MS
}

// ── Verdicts ────────────────────────────────────────────────────────────────

/**
 * Over/under against a line.
 *
 * Landing exactly on the line is a push (stake back), which the tracker has no
 * status for yet — it only knows won/lost/pending. Rather than mis-settle one
 * as a loss, pushes return null and stay pending for the user to handle. They
 * are rare: every player prop and milestone line is a .5, so only whole-number
 * handicaps can land exactly. Add a 'void' status and this becomes a one-liner.
 */
function overUnder(value, line, side) {
  if (value == null || line == null) return null
  if (value === line) return null  // push
  const lower = String(side).toLowerCase()
  if (lower === 'over') return value > line ? 'won' : 'lost'
  if (lower === 'under') return value < line ? 'won' : 'lost'
  return null  // unknown side, do not guess
}

/**
 * Markets that ask WHETHER rather than HOW MANY: the NRL tryscorer trio, which
 * the feed resolves to a boolean on the player rather than a count, so there is
 * no line to compare against.
 *
 * A non-scorer carries an explicit `false` (verified against a concluded NRL
 * game, where every player who did not score reads false on all three fields),
 * which is what makes losses as safe to settle as wins. A MISSING field is a
 * different thing entirely — a market this sport does not price, or a player
 * who never took the field — and stays null.
 *
 * Only ever called once the game is final. Mid-game a false means nobody has
 * scored YET, not that the bet is losing, which is why liveProgress refuses to
 * draw a verdict for these while the game is running.
 *
 * A side other than yes is refused rather than inverted. No book prices the
 * "not to score" leg of these, so a value there means something we did not
 * anticipate, and guessing at it is exactly what this file exists to avoid.
 */
function whetherVerdict(value, side) {
  if (typeof value !== 'boolean') return null
  const lower = String(side ?? '').toLowerCase()
  if (lower !== '' && lower !== 'yes') return null
  return value ? 'won' : 'lost'
}

/** Which value a bet is asking about, or null if this game cannot answer it. */
function resolveValue(bet, entry) {
  const score = entry.score
  const { base, periods, unreadable } = windowFor(bet)

  if (!base || unreadable) return null

  // Player prop. The results feed names stat fields after the market key, and
  // base_market_type has already had any _milestones suffix stripped upstream.
  // Those stats are whole-match totals with no period breakdown, so a period
  // scoped prop cannot be answered at all and stays manual.
  if (bet.player_name) {
    if (periods) return null
    const player = (entry.players ?? []).find(p => p.name === bet.player_name)
    if (!player) return null
    return player[base] ?? null
  }

  // Totals count whatever the market key names, not the sport's headline score.
  const kind = marketKind(base)
  const unit = unitOf(base)
  if (!unit) return null

  if (kind === 'match_total') {
    // full_time is the authoritative figure for a whole game; a window has
    // nothing to read but the periods themselves.
    return periods
      ? sumWindow(score?.totals?.by_period, periods, unit)
      : (score?.totals?.full_time?.[unit] ?? null)
  }

  if (kind === 'team_total') {
    return periods
      ? sumWindow(teamPeriods(score, bet.team_name), periods, unit)
      : (teamBlock(score, bet.team_name)?.[unit] ?? null)
  }

  return null
}

/**
 * Won or lost for one selection against a game already known to be final.
 *
 * Takes only the selection fields — teams, market, side, point, player/team —
 * so a multi leg is judged by exactly the same rules as the equivalent single.
 * Knows nothing about stake or odds, and does not check whether the game is
 * settleable; both callers below do that first.
 *
 * Returns 'won', 'lost', or null for anything ambiguous.
 */
function selectionVerdict(bet, entry) {
  const score = entry.score
  const { base, periods, unreadable } = windowFor(bet)

  // A period market on a sport that never declared that window. Refusing here
  // is the whole point: falling through would judge it on the full game.
  if (!base || unreadable) return null

  let status = null

  if (marketKind(base) === 'margin') {
    // Both compare the two teams' scores, so both need the sport's score unit.
    const field = SPORT_SCORE_FIELD[bet.sport]
    const backed = teamBlock(score, bet.team_name)
    const opponent = backed === score?.home ? score?.away : score?.home
    if (!field || !backed || !opponent) return null

    // Matching the base means alternate_lines_1st_half is judged by the same
    // rule as alternate_lines, just over its window's periods.
    const backedScore = periods
      ? sumWindow(teamPeriods(score, backed.team), periods, field)
      : backed[field]
    const opponentScore = periods
      ? sumWindow(teamPeriods(score, opponent.team), periods, field)
      : opponent[field]
    if (backedScore == null || opponentScore == null) return null

    // point is already signed, so a -12.5 favourite carries its own minus.
    // head_to_head is the same comparison with no handicap applied.
    const handicap = base === 'alternate_lines' ? bet.point : 0
    if (handicap == null) return null

    const margin = (backedScore - opponentScore) + handicap
    if (margin === 0) return null  // drawn, or landed exactly on the line
    status = margin > 0 ? 'won' : 'lost'

  } else {
    const value = resolveValue(bet, entry)
    // A boolean means this is a whether-market, not a counting one. Both refuse
    // on anything they cannot read, so an unhandled market still falls through
    // to null and stays pending.
    status = typeof value === 'boolean'
      ? whetherVerdict(value, bet.side)
      : overUnder(value, bet.point, bet.side)
  }

  return status
}

/**
 * What a decided bet pays, or null when the numbers behind it are not usable.
 *
 * The bounds are not paranoia. Number(null) is 0 and passes isFinite, so a
 * plain finite check settles a null stake for a profit of 0 and — far worse —
 * turns a WON bet with null odds into (0 - 1) * stake, recording a win as a
 * full loss. Multis make that reachable: a payout table that does not know a
 * leg count yields 0. Odds must clear 1.0 to be a price at all.
 */
function payout(bet, status) {
  const stake = Number(bet.stake)
  const odds = Number(bet.odds)
  if (!Number.isFinite(stake) || stake <= 0) return null
  if (status === 'won' && (!Number.isFinite(odds) || odds <= 1)) return null
  return status === 'won' ? (odds - 1) * stake : -stake
}

/**
 * Settle a single bet against its results game.
 *
 * Returns { status, actual_profit } for a decided bet, or null to leave it
 * pending — which covers an unfinished game, a market we do not handle, a
 * missing player, and anything else ambiguous.
 */
export function settleBet(bet, entry, now = Date.now()) {
  const game = entry?.game ?? entry
  if (!bet || !game || !isSettleable(game, now)) return null

  const status = selectionVerdict(bet, entry)
  if (!status) return null

  const actual_profit = payout(bet, status)
  if (actual_profit == null) return null

  return { status, actual_profit }
}

// ── Multis ──────────────────────────────────────────────────────────────────

/**
 * The legs of a multi, or null if this bet is not one.
 *
 * A leg carries the same structured fields a single does minus stake and odds:
 * home_team, away_team, commence_time, base_market_type, side, point,
 * player_name, team_name, sport. That is what lets one set of market rules
 * serve both, and it is why the builders have to store those fields per leg.
 */
export function legsOf(bet) {
  const legs = bet?.legs
  return Array.isArray(legs) && legs.length > 0 ? legs : null
}

/**
 * Whether a leg carries enough to be matched to a fixture and judged at all.
 * A multi holding even one leg that fails this is never settled: see
 * settleMulti for why a partial read is worse than no read.
 */
export function isStructuredLeg(leg) {
  return !!(
    leg &&
    leg.sport &&
    leg.base_market_type &&
    leg.commence_time &&
    leg.home_team &&
    leg.away_team
  )
}

/**
 * The settlement fields for one selection, lifted off an engine opportunity.
 *
 * Spread into a leg as it is added to a multi, so the leg is complete from the
 * moment it exists rather than reconstructed at save time from a display
 * string. Mirrors what the single-bet track modals copy across; the split on
 * " vs " is safe because the engines build match as "{home} vs {away}".
 *
 * Anything the opportunity does not carry lands as null, and a leg with nulls
 * fails isStructuredLeg, which leaves the multi manual instead of guessing.
 */
export function settlementFields(opp) {
  const [homeTeam, awayTeam] = String(opp?.match ?? '').split(' vs ')
  return {
    home_team:        homeTeam || null,
    away_team:        awayTeam || null,
    base_market_type: opp?.base_market_type ?? null,
    side:             opp?.side ?? null,
    point:            opp?.point ?? null,
    player_name:      opp?.player_name ?? null,
    team_name:        opp?.team_name ?? null
  }
}

/**
 * One leg as it is persisted in bet_data.legs.
 *
 * Listed out rather than spreading whatever the builder happened to hold in
 * state, so the stored shape is a contract this file defines: the settlement
 * fields settleMulti needs, plus enough to render the leg back to the user.
 * Page-only junk (_key, ev_by_legs, game_url) is dropped.
 */
export function legRecord(leg) {
  // `Number(null)` is 0 and passes isFinite, so a null arrived as a leg reading
  // 0 while an undefined one correctly read null. On ev_percentage that 0 is a
  // claim — "this leg is exactly fair" — rather than the absence of one, which
  // is what a leg off the odds screen actually has.
  const num = (v) => {
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  return {
    selection:     leg?.selection ?? null,
    match:         leg?.match ?? null,
    market_type:   leg?.market_type ?? null,
    bookmaker:     leg?.bookmaker ?? null,
    sport:         leg?.sport ?? null,
    commence_time: leg?.commence_time ?? null,
    odds:          num(leg?.odds),
    ev_percentage: num(leg?.ev_percentage),
    ...settlementFields(leg)
  }
}

/**
 * Won or lost for one leg against the results feed, or null while it is
 * undecided — no fixture matched, still playing, inside the settle delay, or
 * any of the ambiguous cases a single would refuse on.
 *
 * `games` may span several days and sports; each leg finds its own fixture.
 */
export function settleLeg(leg, games, now = Date.now()) {
  if (!isStructuredLeg(leg)) return null

  const entry = findGameForBet(leg, games)
  if (!entry) return null

  const game = entry.game ?? entry
  if (!isSettleable(game, now)) return null

  return selectionVerdict(leg, entry)
}

/** Whether a bet or a single leg is under way, so a result could exist for it. */
function hasStarted(unit, now) {
  const startMs = asMs(unit?.commence_time)
  return Number.isFinite(startMs) && startMs <= now
}

/**
 * Bets worth looking up results for: pending, structured, started, in a sport
 * the results feed covers. `supportedSports` is passed in rather than known
 * here so the sport list stays in useAutoSettle.js where it is documented.
 *
 * ARB, MIDDLE and PROMO carry a known profit from the moment they are entered
 * and are settled there, so they are excluded outright.
 */
export function settleableCandidates(bets, supportedSports, now = Date.now()) {
  return (bets ?? []).filter(bet => {
    if (bet?.status !== 'pending') return false
    if (bet.type !== 'EV') return false

    const legs = legsOf(bet)
    if (legs) {
      // settleMulti refuses a multi with any unreadable leg, so there is
      // nothing to gain by polling for one.
      if (!legs.every(isStructuredLeg)) return false
      if (!legs.every(leg => supportedSports.includes(leg.sport))) return false
      // A multi is decided the moment its FIRST leg loses, so it becomes a
      // candidate as soon as ANY leg has started, not once they all have.
      return legs.some(leg => hasStarted(leg, now))
    }

    // Saved before the engines emitted structured fields, or added by hand.
    // Nothing to match on, so these stay manual.
    if (!bet.base_market_type || !bet.commence_time) return false
    if (!supportedSports.includes(bet.sport)) return false
    return hasStarted(bet, now)
  })
}

/**
 * Everything that needs a fixture looked up: a single bet, or each leg of a
 * multi. Legs that have not started yet are dropped — no result can exist for
 * them, so fetching their sport and date range would be wasted.
 */
export function fixtureUnits(bet, now = Date.now()) {
  const legs = legsOf(bet)
  return (legs ?? [bet]).filter(unit => hasStarted(unit, now))
}

/**
 * Settle a multi. Every leg won -> won. ANY leg lost -> lost, straight away,
 * without waiting on legs whose games have not even started. Anything else
 * stays pending.
 *
 * Legs sit in different games on different days, so a multi is normally decided
 * across several polls: it may go lost on a Friday leg while Sunday's is still
 * scheduled, which is correct — nothing later can rescue it.
 *
 * The one asymmetry to avoid: settling losses off legs we can read while wins
 * wait forever on a leg we can never read. That would quietly bias P&L
 * downwards, so a multi with any unreadable leg (missing structured fields) is
 * refused outright rather than settled half-way.
 */
export function settleMulti(bet, games, now = Date.now()) {
  const legs = legsOf(bet)
  if (!legs) return null
  if (!legs.every(isStructuredLeg)) return null

  const verdicts = legs.map(leg => settleLeg(leg, games, now))

  let status = null
  if (verdicts.includes('lost')) status = 'lost'
  else if (verdicts.every(v => v === 'won')) status = 'won'
  if (!status) return null

  const actual_profit = payout(bet, status)
  if (actual_profit == null) return null

  return { status, actual_profit }
}

// ── Live display ────────────────────────────────────────────────────────────

/**
 * Where a pending bet currently stands in its game.
 *
 * DISPLAY ONLY. Nothing here decides anything — a live number is never a final
 * one, and settleBet is the single path that can change a bet's status.
 *
 * Covers LIVE and CONCLUDED. A concluded game still holds the bet pending for
 * SETTLE_DELAY_MS (the stat-correction window, see isSettleable) — up to an
 * hour where the game is over but the bet has not settled yet. Dropping the
 * display the instant status flips to CONCLUDED would blank the row for that
 * whole hour, so it keeps showing with `final: true` instead.
 *
 * Returns { scoreline, current, line, period, label, final } or null when the
 * game hasn't started, or the market has no meaningful running value. `label`
 * is the ready to render sentence; `current` and `line` are the raw numbers
 * behind it.
 */
export function liveProgress(bet, entry) {
  const game = entry?.game ?? entry
  if (!bet || !game) return null
  if (game.status !== 'LIVE' && game.status !== 'CONCLUDED') return null

  const final = game.status === 'CONCLUDED'
  const score = entry.score
  const field = SPORT_SCORE_FIELD[bet.sport]
  if (!score || !field) return null

  const home = score.home
  const away = score.away
  const scoreline = (home && away)
    ? `${home.team} ${home[field]} - ${away[field]} ${away.team}`
    : null

  // Same scoreline broken up, so the row can emphasise the team actually
  // backed instead of making the user re-read the selection to work it out.
  const teams = (home && away)
    ? {
        home: { team: home.team, score: home[field] },
        away: { team: away.team, score: away[field] }
      }
    : null

  // Period state drives the segmented bar. `period_state` carries one entry per
  // period reached, so the last one is the period in play (or the final one).
  const states = Array.isArray(score.period_state) ? score.period_state : []
  const currentState = states.length ? states[states.length - 1] : null
  const period = currentState?.period ?? null

  const shape = SPORT_PERIODS[bet.sport] ?? null
  // Everything before the period in play is finished by definition, so the bar
  // does not depend on `completed` being present. Some payloads carry only a
  // period number, and a bar sitting empty through a live game reads as broken.
  const completedPeriods = final
    ? (shape?.segments ?? states.length)
    : Math.max(states.filter(p => p.completed).length, period != null ? period - 1 : 0)

  // How far through the period in play, for the partially filled segment. A
  // clock past the nominal length (stoppage) clamps at full. With no clock at
  // all this stays 0 and the completed segments carry the story, which is
  // honest: half-filling a segment we cannot measure would be inventing data.
  const periodFraction = (() => {
    if (final) return 1
    if (currentState?.completed) return 1
    // Each sport measures its own progress: the field name, the direction it
    // counts and the period length all differ, so the arithmetic belongs beside
    // the sport in SPORT_PERIODS rather than here. A sport with no measurable
    // clock returns null and the segment stays empty, which is honest —
    // half-filling something we cannot measure would be inventing data.
    const played = shape?.fraction ? shape.fraction(currentState ?? null) : null
    return played == null ? 0 : Math.max(0, Math.min(1, played))
  })()

  const market = bet.base_market_type
  const { base, windowName } = splitMarket(market)
  const kind = bet.player_name ? null : marketKind(base)

  // Which periods this bet actually covers. Null means the whole game.
  const windowPeriods = windowName ? (shape?.windows?.[windowName] ?? null) : null

  // A period market whose sport never declared that window is unreadable. It
  // renders nothing rather than quietly measuring the full game, which would
  // show a confident number for the wrong thing.
  const windowUnreadable = !!windowName && !windowPeriods

  // The bet's OWN closing moment: full time for a whole-game market, the end of
  // its window otherwise. A first-half bet is decided at the main break with two
  // quarters still to play, so this is deliberately not the same as `final`.
  const decided = windowPeriods
    ? windowPeriods.every(p => p <= completedPeriods)
    : final

  const line = bet.point ?? null
  let current = null
  let label = null

  /**
   * Sum a by-period list, restricted to the bet's window when it has one.
   * Unlike settlement's sumWindow this tolerates periods not yet played, since
   * a running total mid-game is exactly the point. It is display only and can
   * never decide a bet.
   */
  const sumPeriods = (rows, unit) => {
    if (!Array.isArray(rows) || !unit) return null
    const inWindow = windowPeriods ? rows.filter(r => windowPeriods.includes(r.period)) : rows
    if (inWindow.length === 0) return null
    return inWindow.reduce((sum, r) => sum + (r[unit] ?? 0), 0)
  }

  // "24 of 60.5" — how far along an accumulating stat is against its line.
  const against = (value) =>
    value == null ? null : `${value}${line != null ? ` of ${line}` : ''}`

  if (windowUnreadable) {
    // Nothing readable; falls through with current null and no meter.

  } else if (bet.player_name) {
    // Player stats arrive as whole-match totals with no period breakdown, so a
    // period-scoped player prop cannot be read from this feed at all and stays
    // manual. Whole-game props are the common case and work as before.
    if (!windowPeriods) {
      const player = (entry.players ?? []).find(p => p.name === bet.player_name)
      const value = player ? (player[base] ?? null) : null

      // A whether-market (the NRL tryscorers) resolves to a boolean, and while
      // the game is running a false only means nobody has scored YET. There is
      // no honest meter for that: filling a bar would imply a quantity that
      // does not exist, and colouring it would call a bet lost that is still
      // very much alive. It draws no meter at all and leaves the game header to
      // carry the row. `current` staying null is what keeps winningNow() from
      // claiming a verdict.
      if (typeof value !== 'boolean') {
        current = value
        label = against(current)
      }
    }

  } else if (kind === 'match_total') {
    // full_time is deliberately null until the game ends, so the running total
    // is summed from the periods played, which also gives windows for free.
    // Counts the unit named in the market key, not the sport's headline score.
    current = sumPeriods(score.totals?.by_period, unitOf(base))
    label = against(current)

  } else if (kind === 'team_total') {
    // For a whole game the team block is the authoritative running total; for a
    // window there is nothing to read but the periods themselves.
    current = windowPeriods
      ? sumPeriods(teamPeriods(score, bet.team_name), unitOf(base))
      : (teamBlock(score, bet.team_name)?.[unitOf(base)] ?? null)
    label = against(current)

  } else if (kind === 'margin') {
    // Running margin from the backed team's point of view. On its own that is
    // an unreadable signed number: -31 on a North Melbourne bet does not say
    // that St Kilda are the ones 31 in front, so the label names whoever leads.
    const backed = teamBlock(score, bet.team_name)
    const other = backed === home ? away : home

    if (backed && other) {
      let margin = null
      if (windowPeriods) {
        // The sport's score field, the same one selectionVerdict compares on.
        // Omitting it made sumPeriods bail on its own !unit guard, so every
        // period-scoped margin market (a 1st-half head to head or handicap)
        // drew no meter at all while its game was live.
        const forBacked = sumPeriods(teamPeriods(score, backed.team), field)
        const forOther = sumPeriods(teamPeriods(score, other.team), field)
        margin = (forBacked == null || forOther == null) ? null : forBacked - forOther
      } else if (backed[field] != null && other[field] != null) {
        margin = backed[field] - other[field]
      }

      if (margin != null) {
        current = margin
        label = margin === 0
          ? 'scores level'
          : `${(margin > 0 ? backed : other).team} by ${Math.abs(margin)}`
      }
    }
  }

  // Which meter renders. Player props are an accumulating value and resolve
  // without a registry entry, which is what keeps them free for a new sport.
  const meter = windowUnreadable
    ? null
    : bet.player_name
      ? (windowPeriods ? null : 'progress')
      : (METER_FOR_KIND[kind] ?? null)

  return {
    scoreline, current, line, period, label, final,
    // `final` is the GAME being over; `decided` is THIS BET's window having
    // closed, which for a first-half market happens at the main break. The row
    // freezes and starts saying "awaiting settlement" on `decided`, not `final`.
    decided,
    windowEndLabel: windowName ? (WINDOW_END_LABEL[windowName] ?? null) : null,
    // Meter inputs.
    meter,
    // Carried for the row's headshot; the meter itself never uses them.
    playerName: bet.player_name ?? null,
    sport: bet.sport ?? null,
    side: bet.side ?? null,
    backedTeam: bet.team_name ?? null,
    teams,
    // Period bar inputs. `segments` is null for a sport with no config, which
    // the bar treats as "no game context available" rather than guessing.
    segments: shape?.segments ?? null,
    periodLabel: (shape && period != null) ? shape.label(period) : null,
    // The whole state row, not just its seconds: AFL's live position is a time
    // and MLB's is which half of the inning is being played, so each sport's
    // formatter reads the field it actually has.
    clock: shape?.clock ? shape.clock(currentState ?? null) : null,
    completedPeriods,
    periodFraction
  }
}

/**
 * Per-leg live state for a multi, or null if this bet is not one.
 *
 * A multi has one fixture per leg, which is why it cannot reuse the single
 * path: `liveGames` maps a bet to one entry, so a multi is stored as
 * { legEntries: [...] } aligned by index with `bet.legs`.
 *
 * Each leg is run through the SAME liveProgress a single uses. Legs carry the
 * same structured fields (see legRecord), so a leg is displayed by exactly the
 * rules that settle it, which is the same reason settleLeg shares
 * selectionVerdict.
 *
 * `state` is for the summary line, and is display only:
 *   pending  — not started, or no fixture matched
 *   live     — under way, `live` carries the meter
 *   decided  — this leg's window has closed; `winning` is then its outcome
 */
export function liveLegs(bet, matched, now = Date.now()) {
  const legs = legsOf(bet)
  if (!legs) return null

  const entries = matched?.legEntries ?? []

  return legs.map((leg, i) => {
    const live = liveProgress(leg, entries[i])
    const started = hasStarted(leg, now)

    // Legs are grouped by fixture for display, so a same-game multi shows one
    // game header instead of repeating the identical period bar per leg. Keyed
    // off the LEG rather than the matched game so unstarted legs, which have no
    // matched game at all, still group with their siblings. Two meetings of the
    // same pair are separated by the start time.
    const gameLabel = leg?.match || [leg?.home_team, leg?.away_team].filter(Boolean).join(' v ') || null

    return {
      leg,
      live,
      started,
      gameLabel,
      gameKey: `${gameLabel ?? `leg-${i}`}|${leg?.commence_time ?? ''}`,
      state: !live ? 'pending' : (live.decided ? 'decided' : 'live')
    }
  })
}
